(() => {
  const ROOT_SELECTOR = '[data-phc-mariana-facilitator-root]';
  const READY_FLAG = 'phcMarianaFacilitatorReady';

  const first = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');


  const uniqueUrls = (values) => {
    const seen = new Set();
    return values
      .map((value) => String(value || '').trim())
      .filter((value) => {
        if (!value || seen.has(value)) return false;
        seen.add(value);
        return true;
      });
  };

  const copyImagePresentation = (target, template, alt) => {
    if (template) {
      Array.from(template.attributes).forEach((attribute) => {
        if (['src', 'srcset', 'sizes', 'loading', 'hidden', 'alt'].includes(attribute.name)) return;
        target.setAttribute(attribute.name, attribute.value);
      });
    }
    target.alt = alt || template?.alt || '';
    target.decoding = 'async';
  };

  const imageAttemptUrl = (source, attempt, revision = '53') => {
    try {
      const url = new URL(source, window.location.href);
      url.searchParams.set('shopify_theme', revision);
      if (attempt > 0) url.searchParams.set('image_retry', String(attempt));
      return url.toString();
    } catch (_) {
      return source;
    }
  };

  const loadOneImage = (source, { template = null, alt = '', timeoutMs = 15000, attempt = 0, revision = '53' } = {}) => new Promise((resolve) => {
    const candidate = new Image();
    let settled = false;
    copyImagePresentation(candidate, template, alt);

    const finish = (ok) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      candidate.onload = null;
      candidate.onerror = null;
      resolve(ok && candidate.naturalWidth > 0 ? candidate : null);
    };

    const timer = window.setTimeout(() => finish(false), timeoutMs);
    candidate.onload = () => finish(true);
    candidate.onerror = () => finish(false);
    candidate.src = imageAttemptUrl(source, attempt, revision);
    if (candidate.complete && candidate.naturalWidth > 0) finish(true);
  });

  const loadImageCandidates = async (sources, options = {}) => {
    const candidates = uniqueUrls(sources || []);
    const retries = Math.max(0, Math.min(Number(options.retries ?? 1), 2));

    // Try every available Mariana rendition before retrying any one of them.
    // This prevents one bad thumbnail from blocking a perfectly valid large
    // portrait for 15-30 seconds.
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      for (const source of candidates) {
        const image = await loadOneImage(source, { ...options, attempt });
        if (image) return { image, source };
      }
      if (attempt < retries) await new Promise((resolve) => window.setTimeout(resolve, 250 * (attempt + 1)));
    }
    return null;
  };

  const getPath = (obj, path) => {
    if (!obj || !path) return undefined;
    return path.split('.').reduce((value, key) => (value && typeof value === 'object' ? value[key] : undefined), obj);
  };

  const pick = (obj, paths) => {
    for (const path of paths) {
      const value = getPath(obj, path);
      if (value !== undefined && value !== null && String(value).trim() !== '') return value;
    }
    return undefined;
  };

  const asTextList = (value) => {
    if (Array.isArray(value)) {
      return value
        .map((item) => (typeof item === 'object' ? first(item.name, item.title, item.label, item.value) : item))
        .filter(Boolean)
        .join(' | ');
    }
    return value == null ? '' : String(value);
  };

  // Employee/instructor location access in Mariana is not guaranteed to use
  // one flat key. Depending on endpoint/include shape it may arrive as a
  // location object, a list of locations, home_location, location_access,
  // location_permissions, or a JSON:API relationship. Resolve names only;
  // never expose numeric resource IDs as storefront labels.
  const asLocationText = (value, depth = 0, seen = new WeakSet()) => {
    if (value == null || depth > 7) return '';
    if (typeof value === 'string') {
      const text = value.trim();
      return /^\d+$/.test(text) ? '' : text;
    }
    if (typeof value === 'number') return '';
    if (Array.isArray(value)) {
      const labels = value
        .map((item) => asLocationText(item, depth + 1, seen))
        .flatMap((text) => String(text || '').split('|'))
        .map((text) => text.trim())
        .filter(Boolean);
      return [...new Set(labels)].join(' | ');
    }
    if (typeof value !== 'object' || seen.has(value)) return '';
    seen.add(value);

    for (const key of ['name', 'location_name', 'locationName', 'display_name', 'displayName', 'title', 'label']) {
      const candidate = value[key];
      if (typeof candidate === 'string' && candidate.trim() && !/^\d+$/.test(candidate.trim())) return candidate.trim();
    }

    const containers = [
      'locations', 'location', 'home_location', 'homeLocation',
      'location_access', 'locationAccess', 'location_permissions', 'locationPermissions',
      'accessible_locations', 'accessibleLocations', 'employee_locations', 'employeeLocations',
      'studios', 'sites', 'venues', 'data', 'attributes', 'relationships'
    ];
    for (const key of containers) {
      if (value[key] == null) continue;
      const resolved = asLocationText(value[key], depth + 1, seen);
      if (resolved) return resolved;
    }
    return '';
  };


  const looksLikeMediaUrl = (value) => {
    const candidate = String(value || '').trim();
    if (!candidate) return false;
    // Mariana relationships can expose an image resource ID where the actual
    // media URL lives in `included`. Never mistake a bare numeric/UUID ID for
    // an image URL.
    if (/^\d+$/.test(candidate)) return false;
    if (/^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(candidate)) return false;
    return /^(?:https?:)?\/\//i.test(candidate)
      || candidate.startsWith('/')
      || /^(?:media|uploads?|images?|files?|assets?|static|content)\//i.test(candidate)
      || /\.(?:avif|gif|jpe?g|png|webp)(?:[?#].*)?$/i.test(candidate);
  };

  const mediaUrlFromValue = (value, depth = 0, seen = new WeakSet()) => {
    if (!value || depth > 7) return '';
    if (typeof value === 'string') return looksLikeMediaUrl(value) ? value.trim() : '';
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = mediaUrlFromValue(item, depth + 1, seen);
        if (found) return found;
      }
      return '';
    }
    if (typeof value !== 'object' || seen.has(value)) return '';
    seen.add(value);

    for (const key of [
      'url', 'src', 'href', 'uri', 'path', 'absolute_url', 'absoluteUrl',
      'public_url', 'publicUrl', 'secure_url', 'secureUrl', 'download_url', 'downloadUrl',
      'content_url', 'contentUrl', 'source_url', 'sourceUrl', 'location',
      'original', 'original_url', 'originalUrl', 'original_file', 'originalFile',
      'full', 'full_url', 'fullUrl', 'large', 'large_url', 'largeUrl',
      'thumbnail', 'thumbnail_url', 'thumbnailUrl', 'image_url', 'imageUrl',
      'photo_url', 'photoUrl', 'profile_photo_url', 'profilePhotoUrl',
      'cdn_url', 'cdnUrl', 'file_url', 'fileUrl'
    ]) {
      const candidate = value[key];
      if (typeof candidate === 'string' && looksLikeMediaUrl(candidate)) return candidate.trim();
    }

    // Mariana media may be represented as a nested JSON:API/resource object,
    // e.g. profile_photo -> data/attributes -> url. Follow those containers.
    for (const key of ['attributes', 'data', 'file', 'asset', 'media', 'photo', 'image', 'large_image', 'largeImage', 'thumbnail_image', 'thumbnailImage', 'renditions', 'sizes']) {
      if (value[key] && typeof value[key] === 'object') {
        const found = mediaUrlFromValue(value[key], depth + 1, seen);
        if (found) return found;
      }
    }

    return '';
  };

  const findImageUrlDeep = (value, depth = 0, seen = new WeakSet()) => {
    if (!value || typeof value !== 'object' || depth > 5 || seen.has(value)) return '';
    seen.add(value);

    if (Array.isArray(value)) {
      for (const item of value) {
        const found = findImageUrlDeep(item, depth + 1, seen);
        if (found) return found;
      }
      return '';
    }

    for (const [key, child] of Object.entries(value)) {
      const normalizedKey = key.replace(/[^a-z0-9]/gi, '').toLowerCase();
      if (/image|photo|avatar|picture|thumbnail/.test(normalizedKey)) {
        const direct = mediaUrlFromValue(child);
        if (direct) return direct;
      }
    }

    for (const child of Object.values(value)) {
      if (child && typeof child === 'object') {
        const found = findImageUrlDeep(child, depth + 1, seen);
        if (found) return found;
      }
    }

    return '';
  };

  const normalize = (payload, instructorId) => {
    const dataNode = Array.isArray(payload?.data) ? payload.data[0] : payload?.data;
    const raw = payload?.facilitator || payload?.profile || dataNode || payload || {};
    const attrs = raw?.attributes && typeof raw.attributes === 'object' ? { ...raw, ...raw.attributes } : raw;

    const firstName = pick(attrs, [
      'first_name', 'firstName', 'given_name', 'givenName',
      'user.first_name', 'user.firstName', 'user.given_name', 'user.givenName',
      'employee.first_name', 'employee.firstName', 'employee.given_name', 'employee.givenName',
      'person.first_name', 'person.firstName'
    ]);
    const lastName = pick(attrs, [
      'last_name', 'lastName', 'family_name', 'familyName',
      'user.last_name', 'user.lastName', 'user.family_name', 'user.familyName',
      'employee.last_name', 'employee.lastName', 'employee.family_name', 'employee.familyName',
      'person.last_name', 'person.lastName'
    ]);
    const composedName = [firstName, lastName].filter(Boolean).join(' ').trim();

    const imageCandidates = [
      pick(attrs, ['large_image_url', 'largeImageUrl', 'large_image.url', 'largeImage.url']),
      pick(attrs, ['large_image', 'largeImage']),
      pick(attrs, ['profile_image_url', 'profileImageUrl', 'profile_photo_url', 'profilePhotoUrl']),
      pick(attrs, ['profile_photo', 'profilePhoto', 'employee_photo', 'employeePhoto']),
      pick(attrs, ['image_url', 'imageUrl', 'photo_url', 'photoUrl']),
      pick(attrs, ['thumbnail_image_url', 'thumbnailImageUrl', 'thumbnail_image.url', 'thumbnailImage.url']),
      pick(attrs, ['thumbnail_image', 'thumbnailImage']),
      pick(attrs, ['photo_file', 'photoFile', 'image_file', 'imageFile', 'profile_photo_file', 'profilePhotoFile']),
      pick(attrs, ['avatar_url', 'avatarUrl', 'image.url', 'photo.url', 'profile_image.url', 'profileImage.url']),
      pick(attrs, ['profile_photo.url', 'profilePhoto.url', 'picture.url', 'avatar.url']),
      pick(attrs, ['image', 'photo', 'picture', 'avatar']),
      findImageUrlDeep(attrs)
    ];
    const imageUrl = imageCandidates.map((value) => mediaUrlFromValue(value)).find(Boolean) || '';


    const locationValue = pick(attrs, [
      'locations', 'location', 'location_name', 'locationName',
      'home_location', 'homeLocation',
      'location_access', 'locationAccess',
      'location_permissions', 'locationPermissions',
      'accessible_locations', 'accessibleLocations',
      'employee_locations', 'employeeLocations',
      'studios', 'sites', 'venues'
    ]);

    const sessionsValue = pick(attrs, [
      'session_types', 'sessionTypes', 'class_session_types', 'classSessionTypes', 'class_types', 'classTypes', 'sessions', 'formats', 'programs'
    ]);

    const languagesValue = pick(attrs, [
      'languages', 'language', 'spoken_languages', 'spokenLanguages'
    ]);

    const styleValue = pick(attrs, [
      'style', 'styles', 'facilitator_style', 'facilitatorStyle', 'tags'
    ]);

    const socials = attrs.socials || attrs.social || {};
    const normalizeSocialUrl = (network, value) => {
      let url = String(value || '').trim();
      if (!url || url === '#' || /^javascript:/i.test(url)) return '';
      if (network === 'instagram' && !/^https?:\/\//i.test(url)) {
        url = url.replace(/^@/, '').trim();
        if (!url) return '';
        return `https://www.instagram.com/${encodeURIComponent(url)}/`;
      }
      if (!/^https?:\/\//i.test(url)) return '';
      return url;
    };

    const resourceType = String(first(raw?.type, attrs?.type, '') || '');
    const isPublicProfile = /employee_public_profile|instructor_profile/i.test(resourceType);

    return {
      id: String(first(raw?.id, attrs?.id, instructorId) || ''),
      resourceType,
      isPublicProfile,
      name: String(first(pick(attrs, [
        'name', 'profile_name', 'profileName', 'display_name', 'displayName', 'full_name', 'fullName',
        'public_name', 'publicName', 'public_profile_name', 'publicProfileName', 'user.name', 'user.display_name', 'user.full_name',
        'employee.name', 'employee.display_name', 'employee.full_name',
        'person.name', 'person.full_name'
      ]), composedName) || ''),
      jobTitle: String(first(pick(attrs, ['job_title', 'jobTitle', 'title', 'role', 'position']), '') || ''),
      location: asLocationText(locationValue),
      sessionTypes: asTextList(sessionsValue),
      languages: asTextList(languagesValue),
      style: asTextList(styleValue),
      positioning: String(first(pick(attrs, ['positioning', 'tagline', 'headline', 'short_description', 'shortDescription']), '') || ''),
      bio: String(first(pick(attrs, ['bio', 'biography', 'public_bio', 'publicBio', 'bio_text', 'bioText', 'profile_bio', 'profileBio', 'profile_description', 'profileDescription', 'short_bio', 'shortBio', 'description', 'about']), '') || ''),
      imageUrl: String(imageUrl || ''),
      bookingUrl: String(first(pick(attrs, ['booking_url', 'bookingUrl', 'schedule_url', 'scheduleUrl']), '') || ''),
      websiteUrl: String(first(pick(attrs, ['website_url', 'websiteUrl', 'website']), '') || ''),
      socials: {
        facebook: normalizeSocialUrl('facebook', first(pick(attrs, ['facebook_url', 'facebookUrl', 'facebook']), socials.facebook, '')),
        instagram: normalizeSocialUrl('instagram', first(pick(attrs, ['instagram_url', 'instagramUrl', 'instagram_handle', 'instagramHandle', 'instagram']), socials.instagram, '')),
        twitter: normalizeSocialUrl('twitter', first(pick(attrs, ['twitter_url', 'twitterUrl', 'x_url', 'xUrl', 'twitter', 'x']), socials.twitter, socials.x, '')),
        tiktok: normalizeSocialUrl('tiktok', first(pick(attrs, ['tiktok_url', 'tiktokUrl', 'tiktok']), socials.tiktok, '')),
        spotify: normalizeSocialUrl('spotify', first(pick(attrs, ['spotify_url', 'spotifyUrl', 'spotify_link', 'spotifyLink', 'spotify']), socials.spotify, ''))
      }
    };
  };

  const profileHasContent = (profile) => Boolean(profile?.name || profile?.imageUrl || profile?.bio || profile?.positioning);


  const mergeProfiles = (primary, fallback) => {
    if (!primary) return fallback || null;
    if (!fallback) return primary;
    return {
      ...fallback,
      ...primary,
      resourceType: primary.resourceType || fallback.resourceType || '',
      isPublicProfile: Boolean(primary.isPublicProfile || fallback.isPublicProfile),
      name: primary.name || fallback.name || '',
      jobTitle: primary.jobTitle || fallback.jobTitle || '',
      location: primary.location || fallback.location || '',
      sessionTypes: primary.sessionTypes || fallback.sessionTypes || '',
      languages: primary.languages || fallback.languages || '',
      style: primary.style || fallback.style || '',
      positioning: primary.positioning || fallback.positioning || '',
      bio: primary.bio || fallback.bio || '',
      largeImageUrl: primary.largeImageUrl || primary.imageUrl || fallback.largeImageUrl || fallback.imageUrl || '',
      thumbnailImageUrl: primary.thumbnailImageUrl || primary.imageUrl || fallback.thumbnailImageUrl || fallback.imageUrl || '',
      imageUrl: primary.imageUrl || fallback.imageUrl || '',
      bookingUrl: primary.bookingUrl || fallback.bookingUrl || '',
      websiteUrl: primary.websiteUrl || fallback.websiteUrl || '',
      socials: {
        facebook: primary.socials?.facebook || fallback.socials?.facebook || '',
        instagram: primary.socials?.instagram || fallback.socials?.instagram || '',
        twitter: primary.socials?.twitter || fallback.socials?.twitter || '',
        tiktok: primary.socials?.tiktok || fallback.socials?.tiktok || '',
        spotify: primary.socials?.spotify || fallback.socials?.spotify || ''
      }
    };
  };

  const normalizeNordic = (value) => String(value || '')
    .replace(/[æÆ]/g, 'ae')
    .replace(/[øØ]/g, 'o')
    .replace(/[åÅ]/g, 'a');

  const normalizeMatchText = (value) => normalizeNordic(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');

  const matchTokens = (value) => normalizeNordic(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .match(/[a-z0-9]+/g) || [];

  const profileMatchesPage = (profile, pageTitle, pageHandle) => {
    const name = String(profile?.name || '').trim();
    if (!name) return false;

    const nameKey = normalizeMatchText(name);
    const titleKey = normalizeMatchText(pageTitle);
    const handleKey = normalizeMatchText(pageHandle);
    const pageKeys = [handleKey, titleKey].filter(Boolean);
    if (!pageKeys.length) return true;

    if (pageKeys.some((key) => key === nameKey || key.includes(nameKey) || nameKey.includes(key))) return true;

    const nameTokens = matchTokens(name).filter((token) => token.length >= 2);
    if (!nameTokens.length) return false;

    const handleTokens = new Set(matchTokens(pageHandle));
    if (handleTokens.size && nameTokens.every((token) => handleTokens.has(token))) return true;

    const titleTokens = new Set(matchTokens(pageTitle));
    if (titleTokens.size && nameTokens.every((token) => titleTokens.has(token))) return true;

    return false;
  };


  const resourceKey = (resource) => {
    if (!resource || typeof resource !== 'object') return '';
    const type = String(resource.type || '');
    const id = resource.id === undefined || resource.id === null ? '' : String(resource.id);
    return type && id ? `${type}:${id}` : '';
  };

  const buildResourceIndex = (payload) => {
    const index = new Map();
    const add = (resource) => {
      const key = resourceKey(resource);
      if (key && !index.has(key)) index.set(key, resource);
    };
    const data = payload?.data;
    if (Array.isArray(data)) data.forEach(add);
    else add(data);
    if (Array.isArray(payload?.included)) payload.included.forEach(add);
    return index;
  };

  const expandResource = (resource, payload) => {
    if (!resource || typeof resource !== 'object') return resource;
    const index = buildResourceIndex(payload);
    const attrs = resource.attributes && typeof resource.attributes === 'object' ? resource.attributes : {};
    const expanded = { ...resource, ...attrs };
    const relationships = resource.relationships && typeof resource.relationships === 'object' ? resource.relationships : {};

    Object.entries(relationships).forEach(([name, relationship]) => {
      const relationData = relationship?.data;
      if (!relationData) return;
      const resolve = (ref) => {
        if (!ref || typeof ref !== 'object') return ref;
        const resolved = index.get(resourceKey(ref)) || ref;
        const resolvedAttrs = resolved?.attributes && typeof resolved.attributes === 'object' ? resolved.attributes : {};
        return { ...resolved, ...resolvedAttrs };
      };
      expanded[name] = Array.isArray(relationData) ? relationData.map(resolve) : resolve(relationData);
    });

    return expanded;
  };

  const searchableProfileText = (resource) => {
    const values = [];
    const visited = new WeakSet();
    const walk = (value, key = '') => {
      if (value === null || value === undefined) return;
      if (typeof value === 'string' || typeof value === 'number') {
        if (/name|title|display|first|last|given|family|instructor|employee|profile/i.test(key)) values.push(String(value));
        return;
      }
      if (typeof value !== 'object' || visited.has(value)) return;
      visited.add(value);
      if (Array.isArray(value)) return value.forEach((item) => walk(item, key));
      Object.entries(value).forEach(([childKey, child]) => walk(child, childKey));
    };
    walk(resource);
    return normalizeMatchText(values.join(' '));
  };

  const buildEndpoint = (endpoint, instructorId) => {
    if (!endpoint || !instructorId) return '';
    if (endpoint.includes('{id}')) return endpoint.replaceAll('{id}', encodeURIComponent(instructorId));
    try {
      const url = new URL(endpoint, window.location.origin);
      if (!url.searchParams.has('instructor_id')) url.searchParams.set('instructor_id', instructorId);
      return url.toString();
    } catch (_) {
      const join = endpoint.includes('?') ? '&' : '?';
      return `${endpoint}${join}instructor_id=${encodeURIComponent(instructorId)}`;
    }
  };

  const apiRoot = (baseUrl) => {
    if (!baseUrl) return null;
    try {
      const url = new URL(baseUrl, window.location.origin);
      let pathname = url.pathname.replace(/\/+$/, '');
      if (!pathname.endsWith('/api')) pathname += '/api';
      url.pathname = `${pathname}/`;
      url.search = '';
      url.hash = '';
      return url;
    } catch (_) {
      return null;
    }
  };

  const buildApiUrl = (baseUrl, resourcePath, params = {}) => {
    const root = apiRoot(baseUrl);
    if (!root || !resourcePath) return '';
    const cleanPath = String(resourcePath)
      .replace(/^https?:\/\/[^/]+/i, '')
      .replace(/^\/+/, '')
      .replace(/^api\//, '');
    const url = new URL(cleanPath, root);
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value) !== '') url.searchParams.set(key, String(value));
    });
    return url.toString();
  };


  const absoluteMediaUrl = (value, baseUrl) => {
    let url = String(value || '').trim().replace(/&amp;/g, '&');
    if (!url || !looksLikeMediaUrl(url)) return '';
    if (/^\/\//.test(url)) return `${window.location.protocol}${url}`;
    if (/^https?:\/\//i.test(url)) return url;
    try {
      const tenant = new URL(baseUrl || window.location.origin, window.location.origin);
      // Image paths returned by Mariana are media paths, not Admin API paths.
      // Resolve relative media against the tenant origin so `media/...` never
      // becomes the invalid `/api/media/...` URL.
      return new URL(url.replace(/^\.\//, ''), `${tenant.origin}/`).toString();
    } catch (_) {
      return url;
    }
  };

  const buildDirectEndpoint = (baseUrl, pathTemplate, instructorId) => {
    if (!baseUrl || !pathTemplate || !instructorId) return '';
    const path = pathTemplate.includes('{id}')
      ? pathTemplate.replaceAll('{id}', encodeURIComponent(instructorId))
      : pathTemplate;
    return buildApiUrl(baseUrl, path);
  };

  const buildProxyUrl = (proxyEndpoint, targetUrl) => {
    if (!proxyEndpoint || !targetUrl) return '';
    try {
      const proxyUrl = new URL(proxyEndpoint, window.location.origin);
      proxyUrl.searchParams.set('url', targetUrl);
      return proxyUrl.toString();
    } catch (_) {
      return '';
    }
  };

  const requestJson = async (url, proxyEndpoint) => {
    if (!url) throw new Error('Missing Mariana URL');
    if (!proxyEndpoint) throw new Error('Missing secure Mariana proxy URL');

    const requestUrl = buildProxyUrl(proxyEndpoint, url);
    if (!requestUrl) throw new Error('Invalid secure Mariana proxy URL');

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 12000);
    try {
      const response = await fetch(requestUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        credentials: 'omit',
        cache: 'no-store',
        signal: controller.signal
      });
      if (!response.ok) {
        const error = new Error(`Mariana proxy returned ${response.status}`);
        error.status = response.status;
        throw error;
      }
      return response.json();
    } catch (error) {
      if (error?.name === 'AbortError') throw new Error('Mariana request timed out');
      throw error;
    } finally {
      window.clearTimeout(timeout);
    }
  };


  const normalizedFeedCache = new Map();

  const requestNormalizedFacilitators = async (proxyEndpoint) => {
    if (!proxyEndpoint) throw new Error('Missing secure Mariana proxy URL');
    const cacheKey = String(proxyEndpoint);
    if (normalizedFeedCache.has(cacheKey)) return normalizedFeedCache.get(cacheKey);

    const promise = (async () => {
      const endpoint = new URL(proxyEndpoint, window.location.origin);
      endpoint.search = '';
      endpoint.searchParams.set('action', 'facilitators');
      endpoint.searchParams.set('client', 'shopify-v54');
      endpoint.searchParams.set('v', '14');

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 30000);
      try {
        const response = await fetch(endpoint.toString(), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'omit',
          cache: 'default',
          signal: controller.signal
        });
        if (!response.ok) {
          const error = new Error(`Normalized Mariana feed returned ${response.status}`);
          error.status = response.status;
          throw error;
        }
        const payload = await response.json();
        const rows = Array.isArray(payload?.profiles) ? payload.profiles : [];
        return rows.map((row) => ({
          id: String(first(row?.public_profile_id, row?.id, '') || ''),
          publicProfileId: String(first(row?.public_profile_id, row?.id, '') || ''),
          employeeId: String(first(row?.employee_id, '') || ''),
          employeeActive: row?.employee_active !== false,
          largeImageId: String(first(row?.large_image_id, '') || ''),
          thumbnailImageId: String(first(row?.thumbnail_image_id, '') || ''),
          resourceType: 'employee_public_profile',
          isPublicProfile: true,
          name: String(first(row?.name, '') || ''),
          matchNames: Array.isArray(row?.match_names)
            ? row.match_names.map((value) => String(value || '').trim()).filter(Boolean)
            : [String(first(row?.name, '') || '')].filter(Boolean),
          jobTitle: String(first(row?.job_title, row?.jobTitle, row?.title, row?.role, '') || ''),
          location: asLocationText(first(row?.location, row?.location_names, row?.locations, row?.location_name, row?.locationName, row?.home_location, row?.homeLocation, row?.location_access, row?.locationAccess, row?.location_permissions, row?.locationPermissions, row?.accessible_locations, row?.accessibleLocations, row?.employee_locations, row?.employeeLocations, row?.venues, row?.studios, row?.sites, '')),
          sessionTypes: asTextList(first(row?.session_types, row?.sessionTypes, row?.sessions, row?.class_types, row?.classTypes, row?.formats, row?.programs, '')),
          languages: asTextList(first(row?.languages, row?.language, row?.spoken_languages, row?.spokenLanguages, '')),
          style: asTextList(first(row?.style, row?.styles, row?.facilitator_style, row?.facilitatorStyle, row?.tags, '')),
          positioning: String(first(row?.positioning, row?.tagline, row?.headline, '') || ''),
          bio: String(first(row?.bio, '') || ''),
          largeImageUrl: String(first(row?.large_image, row?.image_url, row?.thumbnail, '') || ''),
          thumbnailImageUrl: String(first(row?.thumbnail, row?.image_url, row?.large_image, '') || ''),
          imageUrl: String(first(row?.image_url, row?.large_image, row?.thumbnail, '') || ''),
          bookingUrl: '',
          websiteUrl: '',
          socials: {
            facebook: '',
            instagram: String(first(row?.instagram, '') || ''),
            twitter: '',
            tiktok: '',
            spotify: String(first(row?.spotify, '') || '')
          }
        })).filter(profileHasContent);
      } catch (error) {
        if (error?.name === 'AbortError') throw new Error('Normalized Mariana facilitator feed timed out');
        throw error;
      } finally {
        window.clearTimeout(timeout);
      }
    })().catch((error) => {
      normalizedFeedCache.delete(cacheKey);
      throw error;
    });

    normalizedFeedCache.set(cacheKey, promise);
    return promise;
  };

  const normalizedDetailCache = new Map();

  const requestNormalizedFacilitatorDetail = async (proxyEndpoint, employeeId) => {
    const safeEmployeeId = String(employeeId || '').trim();
    if (!proxyEndpoint || !safeEmployeeId) return null;

    const cacheKey = `${proxyEndpoint}::employee::${safeEmployeeId}`;
    if (normalizedDetailCache.has(cacheKey)) return normalizedDetailCache.get(cacheKey);

    const promise = (async () => {
      const endpoint = new URL(proxyEndpoint, window.location.origin);
      endpoint.search = '';
      endpoint.searchParams.set('action', 'facilitator');
      endpoint.searchParams.set('employee_id', safeEmployeeId);
      endpoint.searchParams.set('client', 'shopify-v54');
      endpoint.searchParams.set('v', '14');

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 20000);
      try {
        const response = await fetch(endpoint.toString(), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'omit',
          cache: 'default',
          signal: controller.signal
        });
        if (!response.ok) {
          const error = new Error(`Normalized Mariana facilitator detail returned ${response.status}`);
          error.status = response.status;
          throw error;
        }

        const payload = await response.json();
        const row = payload?.profile;
        if (!row || typeof row !== 'object') return null;

        const profile = {
          id: String(first(row?.public_profile_id, row?.id, '') || ''),
          publicProfileId: String(first(row?.public_profile_id, row?.id, '') || ''),
          employeeId: String(first(row?.employee_id, safeEmployeeId, '') || ''),
          employeeActive: row?.employee_active !== false,
          largeImageId: String(first(row?.large_image_id, '') || ''),
          thumbnailImageId: String(first(row?.thumbnail_image_id, '') || ''),
          resourceType: 'employee_public_profile',
          isPublicProfile: true,
          name: String(first(row?.name, '') || ''),
          matchNames: Array.isArray(row?.match_names)
            ? row.match_names.map((value) => String(value || '').trim()).filter(Boolean)
            : [String(first(row?.name, '') || '')].filter(Boolean),
          jobTitle: String(first(row?.job_title, row?.jobTitle, row?.title, row?.role, '') || ''),
          location: asLocationText(first(row?.location, row?.location_names, row?.locations, row?.location_name, row?.locationName, row?.home_location, row?.homeLocation, row?.location_access, row?.locationAccess, row?.location_permissions, row?.locationPermissions, row?.accessible_locations, row?.accessibleLocations, row?.employee_locations, row?.employeeLocations, row?.venues, row?.studios, row?.sites, '')),
          sessionTypes: asTextList(first(row?.session_types, row?.sessionTypes, row?.sessions, row?.class_types, row?.classTypes, row?.formats, row?.programs, '')),
          languages: asTextList(first(row?.languages, row?.language, row?.spoken_languages, row?.spokenLanguages, '')),
          style: asTextList(first(row?.style, row?.styles, row?.facilitator_style, row?.facilitatorStyle, row?.tags, '')),
          positioning: String(first(row?.positioning, row?.tagline, row?.headline, '') || ''),
          bio: String(first(row?.bio, '') || ''),
          largeImageUrl: String(first(row?.large_image, row?.image_url, row?.thumbnail, '') || ''),
          thumbnailImageUrl: String(first(row?.thumbnail, row?.image_url, row?.large_image, '') || ''),
          imageUrl: String(first(row?.image_url, row?.large_image, row?.thumbnail, '') || ''),
          bookingUrl: '',
          websiteUrl: '',
          socials: {
            facebook: '',
            instagram: String(first(row?.instagram, '') || ''),
            twitter: '',
            tiktok: '',
            spotify: String(first(row?.spotify, '') || '')
          }
        };

        return profileHasContent(profile) ? profile : null;
      } catch (error) {
        if (error?.name === 'AbortError') throw new Error('Normalized Mariana facilitator detail timed out');
        throw error;
      } finally {
        window.clearTimeout(timeout);
      }
    })().catch((error) => {
      normalizedDetailCache.delete(cacheKey);
      throw error;
    });

    normalizedDetailCache.set(cacheKey, promise);
    return promise;
  };

  const matchNormalizedProfile = (profiles, { pageTitle = '', pageHandle = '' } = {}) => {
    if (!Array.isArray(profiles) || !profiles.length) return null;

    // Shopify page title is authoritative. Mariana supplies one or more safe
    // public-facing aliases for the linked employee. Exact matches win; a
    // shorter display name (e.g. "Alberte" vs "Alberte Hovgaard") is accepted
    // only when it produces one unique best result.
    const titleKey = normalizeMatchText(pageTitle);
    const handleKey = normalizeMatchText(pageHandle);
    const titleTokens = matchTokens(pageTitle);
    const handleTokens = matchTokens(pageHandle);
    if (!titleKey && !handleKey) return null;

    const scoreAlias = (alias) => {
      const raw = String(alias || '').trim();
      const key = normalizeMatchText(raw);
      if (!key) return 0;
      const aliasTokens = matchTokens(raw);

      if (titleKey && key === titleKey) return 1200;
      if (handleKey && key === handleKey) return 1150;

      const sameFirstAsTitle = Boolean(aliasTokens[0] && titleTokens[0] && aliasTokens[0] === titleTokens[0]);
      const sameFirstAsHandle = Boolean(aliasTokens[0] && handleTokens[0] && aliasTokens[0] === handleTokens[0]);
      const aliasInTitle = aliasTokens.length > 0 && aliasTokens.every((token) => titleTokens.includes(token));
      const titleInAlias = titleTokens.length > 0 && titleTokens.every((token) => aliasTokens.includes(token));
      const aliasInHandle = aliasTokens.length > 0 && aliasTokens.every((token) => handleTokens.includes(token));
      const handleInAlias = handleTokens.length > 0 && handleTokens.every((token) => aliasTokens.includes(token));

      if (sameFirstAsTitle && (aliasInTitle || titleInAlias)) {
        return 900 + Math.min(aliasTokens.length, titleTokens.length) * 10;
      }
      if (sameFirstAsHandle && (aliasInHandle || handleInAlias)) {
        return 850 + Math.min(aliasTokens.length, handleTokens.length) * 10;
      }
      return 0;
    };

    const scored = profiles
      .map((profile) => {
        const aliases = [profile?.name, ...(Array.isArray(profile?.matchNames) ? profile.matchNames : [])]
          .map((value) => String(value || '').trim())
          .filter(Boolean);
        const score = aliases.reduce((best, alias) => Math.max(best, scoreAlias(alias)), 0);
        return score > 0 ? { profile, score } : null;
      })
      .filter(Boolean)
      .sort((left, right) => right.score - left.score);

    if (!scored.length) return null;
    if (scored.length > 1 && scored[0].score === scored[1].score) return null;
    return scored[0].profile;
  };

  const resourceLooksLikeProfile = (resource, contextKey = '') => {
    if (!resource || typeof resource !== 'object' || Array.isArray(resource)) return false;
    const type = String(resource.type || '').toLowerCase();
    const context = String(contextKey || '').toLowerCase();
    if (type.includes('employee_public_profile') || type.includes('instructor_profile')) return true;
    if (context.includes('employee_public_profile') || context.includes('instructor')) return true;

    const attrs = resource.attributes && typeof resource.attributes === 'object' ? resource.attributes : resource;
    const hasPersonName = Boolean(first(
      attrs.name, attrs.profile_name, attrs.profileName, attrs.display_name, attrs.full_name, attrs.public_name, attrs.first_name, attrs.firstName,
      attrs?.user?.name, attrs?.user?.display_name, attrs?.user?.full_name, attrs?.user?.first_name,
      attrs?.employee?.name, attrs?.employee?.display_name, attrs?.employee?.full_name, attrs?.employee?.first_name
    ));
    const hasProfileField = Boolean(first(attrs.bio, attrs.biography, attrs.profile_image_url, attrs.large_image_url, attrs.thumbnail_image_url, attrs.large_image, attrs.thumbnail_image, attrs.image_url, attrs.photo_url, attrs.avatar_url));
    if ((type.includes('employee') || type.includes('instructor') || context.includes('employee') || context.includes('instructor')) && hasPersonName) return true;
    return hasPersonName && hasProfileField;
  };

  const collectProfileResources = (payload) => {
    const found = [];
    const visited = new WeakSet();

    const walk = (value, contextKey = '') => {
      if (!value || typeof value !== 'object') return;
      if (visited.has(value)) return;
      visited.add(value);

      if (Array.isArray(value)) {
        value.forEach((item) => walk(item, contextKey));
        return;
      }

      const isProfileResource = resourceLooksLikeProfile(value, contextKey);
      if (isProfileResource) found.push(value);
      Object.entries(value).forEach(([key, child]) => {
        // Once a JSON:API profile resource is identified, its attributes belong
        // to that same profile. Do not collect the attributes object again as a
        // second anonymous candidate, which would create a false scoring tie.
        if (isProfileResource && key === 'attributes') return;
        walk(child, key);
      });
    };

    walk(payload);
    return found;
  };

  const collectInstructorRelatedUrls = (payload, baseUrl) => {
    const urls = new Set();
    const visited = new WeakSet();
    const root = apiRoot(baseUrl);

    const addUrl = (value) => {
      if (!value || typeof value !== 'string') return;
      try {
        const url = new URL(value, root || window.location.origin);
        if (root && url.origin !== root.origin) return;
        urls.add(url.toString());
      } catch (_) {
        // Ignore malformed relationship URLs.
      }
    };

    const walk = (value, contextKey = '') => {
      if (!value || typeof value !== 'object') return;
      if (visited.has(value)) return;
      visited.add(value);

      if (Array.isArray(value)) {
        value.forEach((item) => walk(item, contextKey));
        return;
      }

      const key = String(contextKey || '').toLowerCase();
      if ((key.includes('instructor') || key.includes('employee_public_profile') || key === 'public_profile' || key.includes('publicprofile')) && value.links) {
        addUrl(typeof value.links.related === 'object' ? value.links.related.href : value.links.related);
        addUrl(typeof value.links.self === 'object' ? value.links.self.href : value.links.self);
      }
      Object.entries(value).forEach(([childKey, child]) => walk(child, childKey));
    };

    walk(payload);
    return Array.from(urls);
  };

  const collectInstructorProfileIds = (payload) => {
    const ids = new Set();
    const visited = new WeakSet();

    const walk = (value) => {
      if (!value || typeof value !== 'object') return;
      if (visited.has(value)) return;
      visited.add(value);

      if (Array.isArray(value)) {
        value.forEach(walk);
        return;
      }

      const type = String(value.type || '').toLowerCase();
      if (type.includes('employee_public_profile') && value.id !== undefined && value.id !== null) {
        ids.add(String(value.id));
      }
      Object.values(value).forEach(walk);
    };

    walk(payload);
    return Array.from(ids);
  };

  const resourceReferencesEmployeeId = (resource, employeeId) => {
    const wanted = String(employeeId || '').trim();
    if (!wanted || !resource || typeof resource !== 'object') return false;

    const directCandidates = [
      resource.employee_id, resource.employeeId, resource.user_id, resource.userId,
      resource.employee?.id, resource.user?.id, resource.person?.id,
      resource.attributes?.employee_id, resource.attributes?.employeeId,
      resource.attributes?.user_id, resource.attributes?.userId
    ];
    if (directCandidates.some((value) => value !== undefined && value !== null && String(value) === wanted)) return true;

    const relationships = resource.relationships && typeof resource.relationships === 'object'
      ? resource.relationships
      : {};
    return Object.entries(relationships).some(([key, relationship]) => {
      if (!/employee|user|person/i.test(key)) return false;
      const data = relationship?.data;
      const refs = Array.isArray(data) ? data : [data];
      return refs.some((ref) => ref && ref.id !== undefined && ref.id !== null && String(ref.id) === wanted);
    });
  };

  const profileFromPayload = (payload, { instructorId = '', employeeId = '', pageTitle = '', pageHandle = '' } = {}) => {
    const resources = collectProfileResources(payload);
    const candidates = resources
      .map((resource) => {
        const expanded = expandResource(resource, payload);
        return { raw: expanded, profile: normalize(expanded, instructorId) };
      })
      .filter(({ profile }) => profileHasContent(profile));

    if (!candidates.length) {
      const directResource = Array.isArray(payload?.data)
        ? null
        : (payload?.data || payload?.profile || payload?.facilitator || payload);
      if (!directResource || typeof directResource !== 'object') return null;
      const expanded = expandResource(directResource, payload);
      const direct = normalize(expanded, instructorId);
      return profileHasContent(direct) ? direct : null;
    }

    if (instructorId) {
      const idMatch = candidates.find(({ profile, raw }) => {
        if (String(profile.id) === String(instructorId)) return true;
        const rawId = raw?.id === undefined || raw?.id === null ? '' : String(raw.id);
        return rawId === String(instructorId);
      });
      if (idMatch) return idMatch.profile;
    }

    // Mariana Admin's /employees/edit/{id} value is an employee ID, not the
    // employee_public_profile ID. Public-profile resources can reference that
    // employee through a relationship; use it as an exact match before fuzzy
    // page-title matching.
    if (employeeId) {
      const employeeMatch = candidates.find(({ raw }) => resourceReferencesEmployeeId(raw, employeeId));
      if (employeeMatch) return employeeMatch.profile;
    }

    const titleKey = normalizeMatchText(pageTitle);
    const handleKey = normalizeMatchText(pageHandle);
    const titleTokens = matchTokens(pageTitle);
    const handleTokens = matchTokens(pageHandle);
    const meaningfulTokens = Array.from(new Set([...titleTokens, ...handleTokens])).filter((token) => token.length >= 3);

    if (titleKey || handleKey) {
      const scored = candidates.map(({ profile, raw }) => {
        const nameKey = normalizeMatchText(profile.name);
        const deepKey = searchableProfileText(raw);
        let score = 0;

        if (nameKey && titleKey && nameKey === titleKey) score = Math.max(score, 100);
        if (nameKey && handleKey && nameKey === handleKey) score = Math.max(score, 100);
        if (nameKey && titleKey && (nameKey.includes(titleKey) || titleKey.includes(nameKey))) score = Math.max(score, 80);
        if (nameKey && handleKey && (nameKey.includes(handleKey) || handleKey.includes(nameKey))) score = Math.max(score, 75);
        if (deepKey && titleKey && deepKey.includes(titleKey)) score = Math.max(score, 65);
        if (deepKey && handleKey && deepKey.includes(handleKey)) score = Math.max(score, 60);

        const tokenHits = meaningfulTokens.filter((token) => deepKey.includes(normalizeMatchText(token))).length;
        if (tokenHits) score = Math.max(score, 30 + tokenHits * 10);

        const resourceType = String(profile.resourceType || raw?.type || '');
        const priority = profile.isPublicProfile
          ? 2
          : (/employee/i.test(resourceType) ? 1 : 0);

        return { profile, score, priority };
      }).sort((a, b) => (b.score - a.score) || (b.priority - a.priority));

      if (scored[0]?.score >= 40) {
        if (!scored[1] || scored[0].score > scored[1].score || scored[0].priority > scored[1].priority) {
          return scored[0].profile;
        }
      }

      const names = candidates.map(({ profile }) => `${profile.name || '(unnamed)'} [${profile.id || 'no-id'}]`).slice(0, 25);
      if (names.length) console.info('[PHC] Mariana facilitator candidates:', names);
      return null;
    }

    return !instructorId && candidates.length === 1 ? candidates[0].profile : null;
  };

  const fetchPaginatedMatch = async (startUrl, proxyEndpoint, matchOptions, maxPages = 4) => {
    let url = startUrl;
    let lastPayload = null;
    const payloads = [];
    for (let page = 0; url && page < maxPages; page += 1) {
      const payload = await requestJson(url, proxyEndpoint);
      lastPayload = payload;
      payloads.push(payload);
      const profile = profileFromPayload(payload, matchOptions);
      if (profile) return { profile, payload, payloads };
      const next = first(
        payload?.links?.next,
        payload?.meta?.pagination?.links?.next,
        payload?.pagination?.next,
        payload?.next
      );
      const nextValue = typeof next === 'object' ? first(next?.href, next?.url) : next;
      if (!nextValue) {
        url = '';
      } else {
        try {
          url = new URL(nextValue, url).toString();
        } catch (_) {
          url = nextValue;
        }
      }
    }
    return { profile: null, payload: lastPayload, payloads };
  };

  const collectionPayloadCache = new Map();

  const fetchCachedCollectionPayloads = async (startUrl, proxyEndpoint, maxPages = 30) => {
    if (!startUrl || !proxyEndpoint) return [];
    const cacheKey = `${proxyEndpoint}::${startUrl}::${maxPages}`;
    if (collectionPayloadCache.has(cacheKey)) return collectionPayloadCache.get(cacheKey);

    const promise = (async () => {
      const payloads = [];
      let url = startUrl;

      for (let page = 0; url && page < maxPages; page += 1) {
        const payload = await requestJson(url, proxyEndpoint);
        payloads.push(payload);

        const next = first(
          payload?.links?.next,
          payload?.meta?.pagination?.links?.next,
          payload?.pagination?.next,
          payload?.next
        );
        const nextValue = typeof next === 'object' ? first(next?.href, next?.url) : next;
        if (!nextValue) {
          url = '';
        } else {
          try { url = new URL(nextValue, url).toString(); }
          catch (_) { url = String(nextValue); }
        }
      }

      return payloads;
    })().catch((error) => {
      collectionPayloadCache.delete(cacheKey);
      throw error;
    });

    collectionPayloadCache.set(cacheKey, promise);
    return promise;
  };

  const matchProfileAcrossPayloads = (payloads, options) => {
    for (const payload of payloads || []) {
      const profile = profileFromPayload(payload, options);
      if (profile && profileMatchesPage(profile, options?.pageTitle, options?.pageHandle)) return profile;
    }
    return null;
  };

  const isoDate = (date) => date.toISOString().slice(0, 10);

  const fetchMarianaProfile = async ({ baseUrl, proxyEndpoint, pathTemplate, instructorId, pageTitle, pageHandle }) => {
    // The ID used by Mariana Web Integrations (`?instructors=...`) is an
    // instructor/employee identifier. Do NOT assume it is the same ID as an
    // Admin API employee_public_profile resource. Resolve the employee first,
    // then follow any employee_public_profile relationship returned by Mariana.
    const configuredId = String(instructorId || '').trim();
    let profileId = '';
    let employeeId = configuredId;
    let employeeIdValidated = false;
    const errors = [];

    const matchOptions = () => ({
      instructorId: profileId,
      employeeId: employeeIdValidated ? employeeId : '',
      pageTitle,
      pageHandle
    });

    const tryRequest = async (url, options = {}) => {
      if (!url) return null;
      try {
        const result = await fetchPaginatedMatch(url, proxyEndpoint, matchOptions(), options.maxPages || 4);
        if (result?.profile && profileMatchesPage(result.profile, pageTitle, pageHandle)) {
          return { profile: result.profile, payloads: result.payloads || [] };
        }
        if (result?.profile) {
          console.info('[PHC] Ignored Mariana profile because it does not match this facilitator page:', {
            profile: result.profile.name || result.profile.id || '(unnamed)',
            page: pageHandle || pageTitle || '(unknown)'
          });
        }
        return { profile: null, payloads: result?.payloads || [] };
      } catch (error) {
        errors.push(error);
        return { profile: null, payloads: [], error };
      }
    };

    const tryPublicProfileId = async (id) => {
      const wanted = String(id || '').trim();
      if (!wanted) return null;
      const detailUrl = buildApiUrl(baseUrl, `employee_public_profiles/${encodeURIComponent(wanted)}`);
      const result = await tryRequest(detailUrl);
      if (result?.profile) {
        profileId = wanted;
        return result.profile;
      }
      return null;
    };

    const resolveProfileRelations = async (payloads) => {
      const list = Array.isArray(payloads) ? payloads : [payloads];

      // Follow explicit Mariana relationship links first when present.
      for (const relatedUrl of collectInstructorRelatedUrls(list, baseUrl).slice(0, 20)) {
        const result = await tryRequest(relatedUrl);
        if (result?.profile) return result.profile;
      }

      // JSON:API relationships normally expose the real public-profile resource
      // ID even when the parent employee uses a different numeric ID.
      for (const discoveredId of collectInstructorProfileIds(list).slice(0, 40)) {
        const profile = await tryPublicProfileId(discoveredId);
        if (profile) return mergeProfiles(profile, employeeProfile);
      }

      return null;
    };

    // 1) PRIMARY: resolve the ID exactly as Mariana Web Integrations uses it —
    // as an employee/instructor ID. This is the important change from v14.
    let employeeProfile = null;
    if (employeeId) {
      const employeeUrls = [
        buildApiUrl(baseUrl, `employees/${encodeURIComponent(employeeId)}`),
        buildApiUrl(baseUrl, 'employees', { id: employeeId })
      ].filter(Boolean);

      for (const employeeUrl of employeeUrls) {
        const result = await tryRequest(employeeUrl);
        if (result?.profile) {
          employeeIdValidated = true;
          employeeProfile = mergeProfiles(employeeProfile, result.profile);
        }

        const relatedProfile = await resolveProfileRelations(result?.payloads || []);
        if (relatedProfile) return mergeProfiles(relatedProfile, employeeProfile);

        // A 404 on one employee URL should not stop the second employee form.
        if (Number(result?.error?.status) !== 404 && result?.error) break;
      }
    }

    // 2) COMPATIBILITY: if a store has historically saved a real
    // employee_public_profile ID instead of the Web Integration instructor ID,
    // allow that value to continue working — but only after employee resolution.
    if (configuredId) {
      const configuredProfileUrl = buildDirectEndpoint(baseUrl, pathTemplate, configuredId)
        || buildApiUrl(baseUrl, `employee_public_profiles/${encodeURIComponent(configuredId)}`);
      const configuredResult = await tryRequest(configuredProfileUrl);
      if (configuredResult?.profile) {
        profileId = String(configuredResult.profile.id || configuredId);
        return mergeProfiles(configuredResult.profile, employeeProfile);
      }
    }

    // 3) EMPLOYEE DIRECTORY: Shopify pages often know only the facilitator's
    // public name, while Mariana Admin identifies the person as an Employee.
    // Match the employee collection by name/handle, lock that real employee ID,
    // then follow the employee detail's public-profile relationship. This is the
    // reliable path for profiles such as Rune Søgaard where the Admin employee
    // ID and employee_public_profile ID are different namespaces.
    try {
      const employeePayloads = await fetchCachedCollectionPayloads(
        buildApiUrl(baseUrl, 'employees'),
        proxyEndpoint,
        30
      );
      const employeeDirectoryProfile = matchProfileAcrossPayloads(employeePayloads, {
        instructorId: '',
        employeeId: '',
        pageTitle,
        pageHandle
      });

      if (employeeDirectoryProfile) {
        // Some Mariana employee-list responses include the related public
        // profile inline. If that is what matched, keep its public-profile ID;
        // never reinterpret it as an employee ID.
        if (employeeDirectoryProfile.isPublicProfile) {
          return mergeProfiles(employeeDirectoryProfile, employeeProfile);
        }

        employeeId = String(employeeDirectoryProfile.id || '').trim();
        employeeIdValidated = Boolean(employeeId);
        employeeProfile = mergeProfiles(employeeProfile, employeeDirectoryProfile);

        if (employeeId) {
          const employeeDetailResult = await tryRequest(
            buildApiUrl(baseUrl, `employees/${encodeURIComponent(employeeId)}`),
            { maxPages: 2 }
          );
          if (employeeDetailResult?.profile) {
            employeeProfile = mergeProfiles(employeeProfile, employeeDetailResult.profile);
          }
          const employeeRelatedProfile = await resolveProfileRelations(employeeDetailResult?.payloads || []);
          if (employeeRelatedProfile) return mergeProfiles(employeeRelatedProfile, employeeProfile);
        }
      }
    } catch (error) {
      errors.push(error);
    }

    // 4) Public-profile collection fallback. Match by the actual Mariana
    // profile name (for example `profile_name: Simon Inkin`). Cache the full
    // collection so multiple Shopify cards do not refetch every page.
    try {
      const publicPayloads = await fetchCachedCollectionPayloads(
        buildApiUrl(baseUrl, 'employee_public_profiles'),
        proxyEndpoint,
        30
      );
      const collectionProfile = matchProfileAcrossPayloads(publicPayloads, {
        instructorId: profileId,
        employeeId: employeeIdValidated ? employeeId : '',
        pageTitle,
        pageHandle
      });
      if (collectionProfile) return mergeProfiles(collectionProfile, employeeProfile);
    } catch (error) {
      errors.push(error);
    }

    // 5) Documented Admin API fallback: class_sessions is documented and
    // exposes employee_public_profiles as the instructor-profile relationship.
    // Do not filter it with the Web Integration employee ID; discover the real
    // public-profile ID from the session relationship instead.
    const now = new Date();
    const future = new Date(now);
    future.setDate(future.getDate() + 180);
    const futureParams = {
      min_date: isoDate(now),
      max_date: isoDate(future)
    };

    const futureResult = await tryRequest(buildApiUrl(baseUrl, 'class_sessions', futureParams), { maxPages: 6 });
    if (futureResult?.profile) return mergeProfiles(futureResult.profile, employeeProfile);
    const futureRelated = await resolveProfileRelations(futureResult?.payloads || []);
    if (futureRelated) return mergeProfiles(futureRelated, employeeProfile);

    // 6) A facilitator may have no future classes. Search recent history too.
    const past = new Date(now);
    past.setDate(past.getDate() - 180);
    const pastResult = await tryRequest(buildApiUrl(baseUrl, 'class_sessions', {
      min_date: isoDate(past),
      max_date: isoDate(now)
    }), { maxPages: 6 });
    if (pastResult?.profile) return mergeProfiles(pastResult.profile, employeeProfile);
    const pastRelated = await resolveProfileRelations(pastResult?.payloads || []);
    if (pastRelated) return mergeProfiles(pastRelated, employeeProfile);

    const meaningfulError = [...errors].reverse().find((error) => Number(error?.status) !== 404);
    if (employeeProfile && profileHasContent(employeeProfile)) return employeeProfile;

    throw meaningfulError || new Error('No matching Mariana facilitator profile was found');
  };

  const collectImageRelationshipTargets = (payload, baseUrl) => {
    const targets = new Set();
    const visited = new WeakSet();

    const add = (value) => {
      if (!value || typeof value !== 'string') return;
      try {
        const root = apiRoot(baseUrl);
        const url = new URL(value, root || window.location.origin);
        if (root && url.origin !== root.origin) return;
        targets.add(url.toString());
      } catch (_) {
        // Ignore malformed relationship URLs.
      }
    };

    const addRef = (ref) => {
      if (!ref || typeof ref !== 'object') return;
      const type = String(ref.type || '').replace(/^\/+|\/+$/g, '');
      const id = ref.id === undefined || ref.id === null ? '' : String(ref.id);
      if (!type || !id) return;
      const url = buildApiUrl(baseUrl, `${type}/${encodeURIComponent(id)}`);
      if (url) targets.add(url);
    };

    const walk = (value) => {
      if (!value || typeof value !== 'object' || visited.has(value)) return;
      visited.add(value);

      if (Array.isArray(value)) {
        value.forEach(walk);
        return;
      }

      const relationships = value.relationships && typeof value.relationships === 'object'
        ? value.relationships
        : {};
      Object.entries(relationships).forEach(([key, relationship]) => {
        if (!/image|photo|thumbnail|avatar|picture/i.test(key)) return;
        const links = relationship?.links || {};
        add(typeof links.related === 'object' ? links.related?.href : links.related);
        add(typeof links.self === 'object' ? links.self?.href : links.self);
        const data = relationship?.data;
        (Array.isArray(data) ? data : [data]).forEach(addRef);
      });

      Object.values(value).forEach(walk);
    };

    walk(payload);
    return Array.from(targets);
  };

  const imageUrlFromPayload = (payload, baseUrl) => {
    const direct = mediaUrlFromValue(payload) || findImageUrlDeep(payload);
    return direct ? absoluteMediaUrl(direct, baseUrl) : '';
  };

  const enrichProfileImage = async (profile, { baseUrl, proxyEndpoint, pageTitle, pageHandle }) => {
    if (!profile || profile.imageUrl || !profile.id || !baseUrl || !proxyEndpoint) return profile;

    // Only an employee_public_profile ID is safe to use against the public
    // profile detail endpoint. Employee IDs belong to a different namespace.
    if (!profile.isPublicProfile && !/employee_public_profile|instructor_profile/i.test(String(profile.resourceType || ''))) {
      return profile;
    }

    const detailPath = `employee_public_profiles/${encodeURIComponent(profile.id)}`;
    const attempts = [
      buildApiUrl(baseUrl, detailPath),
      buildApiUrl(baseUrl, detailPath, { include: 'large_public_photo,thumbnail_photo' }),
      buildApiUrl(baseUrl, detailPath, { include: 'large_public_photo' }),
      buildApiUrl(baseUrl, detailPath, { include: 'thumbnail_photo' }),
      buildApiUrl(baseUrl, detailPath, { include: 'profile_image' }),
      buildApiUrl(baseUrl, detailPath, { include: 'profile_images' }),
      buildApiUrl(baseUrl, detailPath, { include: 'images' })
    ].filter(Boolean);

    for (const url of attempts) {
      try {
        const payload = await requestJson(url, proxyEndpoint);
        const enriched = profileFromPayload(payload, {
          instructorId: String(profile.id),
          pageTitle,
          pageHandle
        });
        if (enriched?.imageUrl) return mergeProfiles(enriched, profile);

        // Some tenants keep Large Image / Thumbnail in a relationship resource
        // instead of including the URL in the public-profile payload.
        for (const target of collectImageRelationshipTargets(payload, baseUrl).slice(0, 12)) {
          try {
            const imagePayload = await requestJson(target, proxyEndpoint);
            const relatedImageUrl = imageUrlFromPayload(imagePayload, baseUrl);
            if (relatedImageUrl) {
              return {
                ...profile,
                imageUrl: relatedImageUrl,
                isPublicProfile: true
              };
            }
          } catch (_) {
            // Try the next image relationship.
          }
        }
      } catch (_) {
        // Unsupported include/detail variant: try the next harmless form.
      }
    }

    return profile;
  };

  const employeeLocationCache = new Map();

  const locationIdsFromResource = (resource) => {
    const ids = [];
    const relationships = resource?.relationships && typeof resource.relationships === 'object'
      ? resource.relationships
      : {};

    Object.entries(relationships).forEach(([key, relationship]) => {
      if (!/location|studio|site|venue/i.test(key)) return;
      const data = relationship?.data;
      (Array.isArray(data) ? data : [data]).forEach((ref) => {
        const id = ref?.id === undefined || ref?.id === null ? '' : String(ref.id).trim();
        if (id) ids.push(id);
      });
    });
    return [...new Set(ids)];
  };

  const directoryResourceRows = (payloads) => {
    const rows = [];
    (payloads || []).forEach((payload) => {
      const data = Array.isArray(payload?.data) ? payload.data : payload?.data ? [payload.data] : [];
      data.forEach((resource) => {
        if (resource && typeof resource === 'object') rows.push({ resource, expanded: expandResource(resource, payload) });
      });
    });
    return rows;
  };

  const fetchEmployeeLocationMap = async (baseUrl, proxyEndpoint) => {
    if (!baseUrl || !proxyEndpoint) return null;
    const cacheKey = `${baseUrl}::${proxyEndpoint}::employee-locations-v54`;
    if (employeeLocationCache.has(cacheKey)) return employeeLocationCache.get(cacheKey);

    const promise = (async () => {
      const employeePayloads = await fetchCachedCollectionPayloads(
        buildApiUrl(baseUrl, 'employees'),
        proxyEndpoint,
        30
      );
      if (!employeePayloads.length) return null;

      let locationPayloads = [];
      try {
        locationPayloads = await fetchCachedCollectionPayloads(
          buildApiUrl(baseUrl, 'locations'),
          proxyEndpoint,
          10
        );
      } catch (error) {
        console.info('[PHC] Mariana location directory unavailable; using employee location names only:', error?.message || error);
      }

      const locationNames = new Map();
      directoryResourceRows(locationPayloads).forEach(({ resource, expanded }) => {
        const id = String(resource?.id ?? expanded?.id ?? '').trim();
        const label = asLocationText(first(
          expanded?.name,
          expanded?.location_name,
          expanded?.locationName,
          expanded?.display_name,
          expanded?.displayName,
          expanded?.title,
          expanded
        ));
        if (id && label) locationNames.set(id, label);
      });

      const employeeLocations = new Map();
      directoryResourceRows(employeePayloads).forEach(({ resource, expanded }) => {
        const employeeId = String(resource?.id ?? expanded?.id ?? '').trim();
        if (!employeeId) return;

        let location = asLocationText(first(
          expanded?.locations,
          expanded?.location,
          expanded?.location_name,
          expanded?.locationName,
          expanded?.home_location,
          expanded?.homeLocation,
          expanded?.location_access,
          expanded?.locationAccess,
          expanded?.location_permissions,
          expanded?.locationPermissions,
          expanded?.accessible_locations,
          expanded?.accessibleLocations,
          expanded?.employee_locations,
          expanded?.employeeLocations,
          expanded?.studios,
          expanded?.sites,
          expanded?.venues
        ));

        if (!location && locationNames.size) {
          location = locationIdsFromResource(resource)
            .map((id) => locationNames.get(id) || '')
            .filter(Boolean)
            .filter((label, index, labels) => labels.indexOf(label) === index)
            .join(' | ');
        }

        employeeLocations.set(employeeId, {
          location,
          name: String(first(
            expanded?.profile_name,
            expanded?.profileName,
            expanded?.display_name,
            expanded?.displayName,
            expanded?.name,
            [expanded?.first_name, expanded?.last_name].filter(Boolean).join(' ')
          ) || '').trim()
        });
      });

      return employeeLocations;
    })().catch((error) => {
      employeeLocationCache.delete(cacheKey);
      throw error;
    });

    employeeLocationCache.set(cacheKey, promise);
    return promise;
  };

  const enrichDirectoryLocations = async (profiles, baseUrl, proxyEndpoint) => {
    if (!Array.isArray(profiles) || !profiles.length || !baseUrl || !proxyEndpoint) return profiles;
    if (!profiles.some((profile) => !String(profile?.location || '').trim())) return profiles;

    let employeeLocations = null;
    try {
      employeeLocations = await fetchEmployeeLocationMap(baseUrl, proxyEndpoint);
    } catch (error) {
      console.info('[PHC] Mariana employee locations unavailable:', error?.message || error);
      return profiles;
    }
    if (!(employeeLocations instanceof Map)) return profiles;

    const values = Array.from(employeeLocations.entries());
    return profiles.map((profile) => {
      if (String(profile?.location || '').trim()) return { ...profile, employeeLocationLookupComplete: true };

      let match = profile?.employeeId ? employeeLocations.get(String(profile.employeeId)) : null;
      if (!match) {
        const compatible = values.filter(([, employee]) => {
          const a = normalizeMatchText(employee?.name || '');
          const b = normalizeMatchText(profile?.name || '');
          return a && b && (a === b || a.startsWith(b) || b.startsWith(a));
        });
        if (compatible.length === 1) match = compatible[0][1];
      }

      return {
        ...profile,
        location: String(match?.location || '').trim(),
        employeeLocationLookupComplete: true
      };
    });
  };

  const directoryCache = new Map();

  const fetchDirectoryProfiles = async ({ baseUrl, proxyEndpoint, maxProfiles = 250 } = {}) => {
    const limit = Math.max(1, Math.min(Number(maxProfiles) || 250, 500));
    const cacheKey = `${baseUrl}::${proxyEndpoint}::${limit}`;
    if (directoryCache.has(cacheKey)) return directoryCache.get(cacheKey);

    const promise = (async () => {
      if (!proxyEndpoint) return [];

      // Preferred proxy v10 path: the server-side proxy resolves Mariana employees,
      // their public_profile relationships and public image resources once. Shopify receives
      // one normalized facilitator feed rather than making dozens of browser
      // requests that can time out independently.
      try {
        const normalizedProfiles = await requestNormalizedFacilitators(proxyEndpoint);
        if (normalizedProfiles.length) {
          const enrichedProfiles = await enrichDirectoryLocations(normalizedProfiles, baseUrl, proxyEndpoint);
          return enrichedProfiles.slice(0, limit);
        }
      } catch (error) {
        console.info('[PHC] Normalized Mariana facilitator feed unavailable; using legacy API fallback:', error?.message || error);
      }

      // Backward-compatible fallback for an older proxy during deployment.
      if (!baseUrl) return [];
      const attempts = [
        buildApiUrl(baseUrl, 'employee_public_profiles', { include: 'large_public_photo,thumbnail_photo' }),
        buildApiUrl(baseUrl, 'employee_public_profiles')
      ].filter(Boolean);
      let lastError = null;

      for (const startUrl of attempts) {
        try {
          const profiles = [];
          const seen = new Set();
          let url = startUrl;

          for (let page = 0; url && page < 20 && profiles.length < limit; page += 1) {
            const payload = await requestJson(url, proxyEndpoint);
            const resources = Array.isArray(payload?.data) ? payload.data : payload?.data ? [payload.data] : [];

            resources.forEach((resource) => {
              if (!resource || typeof resource !== 'object' || profiles.length >= limit) return;
              const expanded = expandResource(resource, payload);
              const profile = normalize(expanded, resource.id || '');
              if (!profileHasContent(profile)) return;
              const key = String(profile.id || normalizeMatchText(profile.name));
              if (!key || seen.has(key)) return;
              seen.add(key);
              if (profile.imageUrl) profile.imageUrl = absoluteMediaUrl(profile.imageUrl, baseUrl);
              profiles.push(profile);
            });

            const next = first(
              payload?.links?.next,
              payload?.meta?.pagination?.links?.next,
              payload?.pagination?.next,
              payload?.next
            );
            const nextValue = typeof next === 'object' ? first(next?.href, next?.url) : next;
            if (!nextValue) {
              url = '';
            } else {
              try { url = new URL(nextValue, url).toString(); }
              catch (_) { url = String(nextValue); }
            }
          }

          if (profiles.length) return profiles;
        } catch (error) {
          lastError = error;
        }
      }

      if (lastError) throw lastError;
      return [];
    })().catch((error) => {
      directoryCache.delete(cacheKey);
      throw error;
    });

    directoryCache.set(cacheKey, promise);
    return promise;
  };

  const fetchProfile = async ({ baseUrl, proxyEndpoint, pathTemplate = '', instructorId = '', pageTitle = '', pageHandle = '' } = {}) => {
    if (!proxyEndpoint) return null;

    // Use the same normalized feed as the directory first. This ensures the
    // individual profile and overview resolve the same Mariana person, Bio and
    // portrait instead of running different lookup chains in the browser.
    try {
      const normalizedProfiles = await requestNormalizedFacilitators(proxyEndpoint);
      const matched = matchNormalizedProfile(normalizedProfiles, { pageTitle, pageHandle });
      if (matched && profileHasContent(matched)) {
        // The normalized proxy feed already returns the normalized public-profile Bio and image
        // URL for each employee. Use that row directly. Only request employee
        // detail if the feed row is unexpectedly missing Bio or portrait.
        const needsDetail = !String(matched.bio || '').trim() || !String(matched.imageUrl || '').trim() || !String(matched.location || '').trim();
        if (needsDetail && matched.employeeId) {
          try {
            const detail = await requestNormalizedFacilitatorDetail(proxyEndpoint, matched.employeeId);
            if (detail && profileHasContent(detail)) {
              const detailStillMatchesPage = profileMatchesPage(detail, pageTitle, pageHandle)
                || normalizeMatchText(detail.name) === normalizeMatchText(matched.name);
              if (detailStillMatchesPage) return mergeProfiles(detail, matched);
            }
          } catch (error) {
            console.info('[PHC] Mariana employee detail fallback unavailable; using normalized feed row:', error?.message || error);
          }
        }
        return matched;
      }

      // A successful normalized feed with no unique name match means Mariana
      // has no safe public profile for this Shopify page. Keep Shopify fallback
      // content instead of trying a numeric id that may belong to another person.
      if (normalizedProfiles.length) return null;
    } catch (error) {
      console.info('[PHC] Normalized Mariana profile lookup unavailable; using name-only legacy fallback:', error?.message || error);
    }

    // Backward-compatible fallback only when the normalized proxy itself is
    // unavailable. Identity remains page-name based; stale numeric ids are not
    // allowed to select another facilitator.
    if (!baseUrl) return null;
    let profile = await fetchMarianaProfile({
      baseUrl,
      proxyEndpoint,
      pathTemplate,
      instructorId: '',
      pageTitle,
      pageHandle
    });
    if (!profile || !profileHasContent(profile)) return null;
    profile = await enrichProfileImage(profile, { baseUrl, proxyEndpoint, pageTitle, pageHandle });
    if (profile?.imageUrl) profile.imageUrl = absoluteMediaUrl(profile.imageUrl, baseUrl);
    return profile;
  };

  window.PHCMarianaFacilitator = {
    fetchDirectoryProfiles,
    fetchProfile,
    fetchEmployeeDetail: requestNormalizedFacilitatorDetail,
    enrichProfileImage,
    matchProfile: matchNormalizedProfile,
    normalizeIdentity: normalizeMatchText,
    loadImageCandidates
  };

  const setText = (field, value) => {
    const text = String(value || '').trim();
    document.querySelectorAll(`[data-phc-facilitator-field="${field}"]`).forEach((node) => {
      node.textContent = text;
      node.hidden = !text;
    });
  };

  const setBio = (value) => {
    if (!value) return;
    document.querySelectorAll('[data-phc-facilitator-field="bio"]').forEach((node) => {
      node.replaceChildren();
      String(value)
        .split(/\n{2,}/)
        .map((paragraph) => paragraph.trim())
        .filter(Boolean)
        .forEach((paragraph) => {
          const p = document.createElement('p');
          p.textContent = paragraph;
          node.appendChild(p);
        });
      if (!node.childNodes.length) node.textContent = value;
      node.hidden = false;
    });
  };

  const setImage = async (profile, name) => {
    const sources = uniqueUrls([
      profile?.imageUrl,
      profile?.largeImageUrl,
      profile?.thumbnailImageUrl
    ]);

    const containers = Array.from(document.querySelectorAll('[data-phc-facilitator-image-container]'));
    await Promise.all(containers.map(async (container) => {
      // The media column is always present as a deliberate neutral/loading
      // block. A matched Mariana facilitator never falls back to stale Shopify
      // media, but a slow image request also never collapses the hero layout.
      container.hidden = false;
      container.dataset.imageResolution = sources.length ? 'pending' : 'missing';

      const oldMedia = Array.from(container.querySelectorAll('img'));
      oldMedia.forEach((node) => { node.hidden = true; });
      if (!sources.length) return;

      const loaded = await loadImageCandidates(sources, {
        template: oldMedia[0] || null,
        alt: name ? `${name}, Plugin Heat Club facilitator` : '',
        timeoutMs: 15000,
        retries: 1,
        revision: '54'
      });

      if (!loaded?.image) {
        container.dataset.imageResolution = 'missing';
        return;
      }

      const candidate = loaded.image;
      candidate.setAttribute('data-phc-facilitator-live-image', '');
      candidate.dataset.phcMarianaImage = 'true';
      candidate.loading = 'eager';
      candidate.fetchPriority = 'high';
      candidate.hidden = false;

      oldMedia.forEach((node) => node.remove());
      container.appendChild(candidate);
      container.dataset.imageResolution = 'ready';
    }));
  };

  const setSocial = (network, url) => {
    const validUrl = /^https?:\/\//i.test(String(url || '').trim()) ? String(url).trim() : '';
    document.querySelectorAll(`[data-phc-facilitator-social="${network}"]`).forEach((node) => {
      if (!validUrl) {
        node.hidden = true;
        node.setAttribute('aria-hidden', 'true');
        if (node.tagName === 'A') node.removeAttribute('href');
        return;
      }
      if (node.tagName === 'A') {
        node.href = validUrl;
        node.hidden = false;
        node.removeAttribute('aria-hidden');
        return;
      }
      const link = document.createElement('a');
      link.href = validUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.setAttribute('aria-label', network === 'twitter' ? 'X' : network.charAt(0).toUpperCase() + network.slice(1));
      link.dataset.phcFacilitatorSocial = network;
      while (node.firstChild) link.appendChild(node.firstChild);
      node.replaceWith(link);
    });
  };

  const syncSocialNavs = () => {
    document.querySelectorAll('[data-phc-facilitator-socials]').forEach((nav) => {
      const active = nav.querySelector('a[href^="http"]:not([hidden])');
      nav.hidden = !active;
    });
  };

  const setTemplateName = (name) => {
    if (!name) return;
    document.querySelectorAll('[data-phc-facilitator-template]').forEach((node) => {
      node.textContent = String(node.dataset.phcFacilitatorTemplate || '').replaceAll('[name]', name);
    });
  };

  const setLink = (name, url) => {
    if (!url) return;
    document.querySelectorAll(`[data-phc-facilitator-link="${name}"]`).forEach((node) => {
      if (node.tagName === 'A') node.href = url;
    });
  };

  const updateSchema = (profile) => {
    const node = document.querySelector('[data-phc-facilitator-schema]');
    if (!node) return;
    try {
      const schema = JSON.parse(node.textContent || '{}');
      if (profile.jobTitle) schema.jobTitle = profile.jobTitle;
      if (profile.bio) schema.description = profile.bio;
      if (profile.location) schema.workLocation = { '@type': 'Place', name: profile.location };
      if (profile.imageUrl) schema.image = profile.imageUrl;
      node.textContent = JSON.stringify(schema);
    } catch (_) {
      // Keep the server-rendered Shopify fallback schema if parsing fails.
    }
  };

  const sessionRows = (payload) => {
    const data = payload?.data ?? payload?.results ?? [];
    if (Array.isArray(data)) return data.filter((row) => row && typeof row === 'object');
    return data && typeof data === 'object' ? [data] : [];
  };

  const sessionStartValue = (session) => first(
    pick(session, [
      'attributes.start_datetime', 'attributes.startDateTime', 'attributes.start_date_time',
      'attributes.starts_at', 'attributes.startsAt', 'attributes.start_at', 'attributes.startAt',
      'attributes.start_time', 'attributes.startTime', 'attributes.datetime', 'attributes.date_time',
      'attributes.start_date', 'attributes.startDate', 'attributes.date',
      'start_datetime', 'startDateTime', 'start_date_time', 'starts_at', 'startsAt',
      'start_at', 'startAt', 'start_time', 'startTime', 'datetime', 'date_time', 'start_date', 'startDate', 'date'
    ]),
    ''
  );

  const sessionIsCancelled = (session) => {
    const value = first(
      pick(session, [
        'attributes.cancelled', 'attributes.canceled', 'attributes.is_cancelled', 'attributes.isCanceled',
        'attributes.status', 'attributes.state', 'cancelled', 'canceled', 'is_cancelled', 'isCanceled', 'status', 'state'
      ]),
      ''
    );
    if (value === true || value === 1) return true;
    return /^(?:cancelled|canceled)$/i.test(String(value || '').trim());
  };

  const nextSessionDate = (rows) => {
    const dates = rows
      .filter((session) => !sessionIsCancelled(session))
      .map((session) => String(sessionStartValue(session) || '').trim())
      .filter(Boolean)
      .map((value) => {
        const candidate = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T12:00:00` : value;
        const date = new Date(candidate);
        return Number.isNaN(date.getTime()) ? null : date;
      })
      .filter(Boolean)
      .sort((a, b) => a.getTime() - b.getTime());
    return dates[0] || null;
  };

  const facilitatorSessionWindow = async ({ baseUrl, proxyEndpoint, publicProfileId, from, to }) => {
    if (!baseUrl || !proxyEndpoint || !publicProfileId) throw new Error('Missing facilitator session lookup configuration');
    const url = buildApiUrl(baseUrl, 'class_sessions', {
      employee_public_profiles: publicProfileId,
      min_date: isoDate(from),
      max_date: isoDate(to),
      page_size: 100
    });
    const payloads = await fetchCachedCollectionPayloads(url, proxyEndpoint, 10);
    return payloads.flatMap(sessionRows).filter((session) => !sessionIsCancelled(session));
  };

  const facilitatorScheduleAvailability = async ({ profile, baseUrl, proxyEndpoint }) => {
    const publicProfileId = String(profile?.publicProfileId || profile?.id || '').trim();
    if (!publicProfileId) return { state: 'unknown', rows: [], next: null };

    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const weekEnd = new Date(today);
    weekEnd.setDate(weekEnd.getDate() + 7);
    const laterStart = new Date(today);
    laterStart.setDate(laterStart.getDate() + 8);
    const scheduleEnd = new Date(today);
    scheduleEnd.setDate(scheduleEnd.getDate() + 90);

    try {
      const nearRows = await facilitatorSessionWindow({
        baseUrl, proxyEndpoint, publicProfileId, from: today, to: weekEnd
      });
      if (nearRows.length) return { state: 'near', rows: nearRows, next: nextSessionDate(nearRows) };

      const laterRows = await facilitatorSessionWindow({
        baseUrl, proxyEndpoint, publicProfileId, from: laterStart, to: scheduleEnd
      });
      if (laterRows.length) return { state: 'later', rows: laterRows, next: nextSessionDate(laterRows) };

      return { state: 'none', rows: [], next: null };
    } catch (error) {
      // Availability is an enhancement around the official Mariana embed. If
      // the class-session check is temporarily unavailable, fail open and keep
      // the existing calendar instead of hiding a potentially valid schedule.
      console.info('[PHC] Mariana facilitator availability check unavailable; keeping schedule visible:', error?.message || error);
      return { state: 'unknown', rows: [], next: null };
    }
  };

  const formatSessionDate = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    try {
      return new Intl.DateTimeFormat('en-GB', {
        weekday: 'long', day: 'numeric', month: 'long'
      }).format(date);
    } catch (_) {
      return isoDate(date);
    }
  };

  const syncFacilitatorSchedule = async (profile) => {
    // Use the existing Mariana employee identity with the official Web Integration.
    // Do not initialize a custom schedule or create direct iframe URLs.
    const employeeId = String(profile?.employeeId || '').trim();
    const publicProfileId = String(profile?.publicProfileId || profile?.id || '').trim();
    document.querySelectorAll('[data-phc-mariana-facilitator-schedule]').forEach((section) => {
      if (employeeId) section.dataset.marianaResolvedEmployeeId = employeeId;
      if (publicProfileId) section.dataset.marianaResolvedProfileId = publicProfileId;
      const host = section.querySelector('[data-phc-facilitator-official-schedule]');
      if (!host) return;
      const explicitRoute = String(section.dataset.marianaExplicitRoute || '').trim();
      const regionId = String(section.dataset.marianaRegionId || '48541').trim();
      const resolvedEmployee = employeeId || String(section.dataset.marianaInstructorId || '').trim();
      const route = explicitRoute || (`/schedule/daily/${encodeURIComponent(regionId)}` +
        (resolvedEmployee ? `?instructors=${encodeURIComponent(resolvedEmployee)}` : ''));
      // The loader is deferred until a route is available on a facilitator page.
      // Once mounted, Mariana controls navigation, filtering, booking and checkout.
      host.setAttribute('data-mariana-integrations', route);
      section.removeAttribute('data-mariana-awaiting-profile');
      section.hidden = false;
      window.PHCStartMarianaWebIntegrations?.();
    });
  };

  const ensureFacilitatorStandardSchedule = () => {
    // On proxy failure, still show Mariana's official schedule rather than
    // leaving the profile with a blank calendar or reinstating custom UI.
    document.querySelectorAll('[data-phc-mariana-facilitator-schedule][data-mariana-awaiting-profile]').forEach((section) => {
      const host = section.querySelector('[data-phc-facilitator-official-schedule]');
      if (!host) return;
      const explicitRoute = String(section.dataset.marianaExplicitRoute || '').trim();
      const regionId = String(section.dataset.marianaRegionId || '48541').trim();
      const instructorId = String(section.dataset.marianaInstructorId || '').trim();
      const route = explicitRoute || (`/schedule/daily/${encodeURIComponent(regionId)}` +
        (instructorId ? `?instructors=${encodeURIComponent(instructorId)}` : ''));
      host.setAttribute('data-mariana-integrations', route);
      section.removeAttribute('data-mariana-awaiting-profile');
      section.hidden = false;
      window.PHCStartMarianaWebIntegrations?.();
    });
  };

  const applyProfile = async (profile, context = {}) => {
    await syncFacilitatorSchedule(profile, context);
    setText('location', profile.location);
    setText('session_types', profile.sessionTypes);
    setText('positioning', profile.positioning);
    setBio(profile.bio);
    const shopifyName = document.querySelector('[data-phc-facilitator-field="name"]')?.textContent?.trim() || profile.name;
    await setImage(profile, shopifyName);
    setLink('booking', profile.bookingUrl);
    ['facebook', 'instagram', 'twitter', 'tiktok', 'spotify'].forEach((network) => setSocial(network, profile.socials?.[network] || ''));
    syncSocialNavs();
    updateSchema(profile);

    document.documentElement.dataset.phcFacilitatorSource = 'mariana-v66';
    window.dispatchEvent(new CustomEvent('phc:facilitator-loaded', { detail: profile }));
  };

  const hydrate = async (root) => {
    if (!root || root.dataset[READY_FLAG] === 'true') return;
    root.dataset[READY_FLAG] = 'true';

    if (root.dataset.marianaProfileEnabled === 'false') return;

    const instructorId = String(root.dataset.marianaInstructorId || '').trim();
    const pageTitle = String(root.dataset.marianaPageTitle || '').trim();
    const pageHandle = String(root.dataset.marianaPageHandle || '').trim();
    const baseUrl = String(root.dataset.marianaApiBaseUrl || '').trim();
    const pathTemplate = String(root.dataset.marianaProfilePathTemplate || '').trim();
    const proxyEndpoint = String(root.dataset.marianaProxyUrl || '').trim();

    root.setAttribute('aria-busy', 'true');
    root.dataset.marianaProfileStatus = 'loading';

    try {
      let profile = null;

      if (proxyEndpoint) {
        // The normalized secure proxy is the authoritative storefront source. The Mariana API base
        // URL is optional and is only used by the legacy emergency fallback
        // inside fetchProfile when the normalized proxy feed is unavailable.
        profile = await fetchProfile({ baseUrl, proxyEndpoint, pathTemplate, instructorId, pageTitle, pageHandle });
      } else {
        throw new Error('Secure Mariana proxy configuration is missing');
      }

      if (!profile || !profileHasContent(profile)) throw new Error('Mariana facilitator response contained no usable profile fields');
      await applyProfile(profile, { baseUrl, proxyEndpoint });
      if (profile.employeeId) root.dataset.marianaResolvedEmployeeId = String(profile.employeeId);
      if (profile.publicProfileId || profile.id) root.dataset.marianaResolvedProfileId = String(profile.publicProfileId || profile.id);
      root.dataset.marianaProfileStatus = 'loaded';
    } catch (error) {
      root.dataset.marianaProfileStatus = 'unavailable';
      document.querySelectorAll('[data-phc-facilitator-image-container]').forEach((container) => {
        container.hidden = false;
        container.dataset.imageResolution = 'missing';
        container.querySelectorAll('img').forEach((image) => { image.hidden = true; });
      });
      // Never log credentials. The error only contains the failed resource path/status.
      console.warn('[PHC] Mariana facilitator profile unavailable:', error);
    } finally {
      ensureFacilitatorStandardSchedule();
      root.removeAttribute('aria-busy');
    }
  };

  const boot = (scope = document) => {
    const roots = scope.matches?.(ROOT_SELECTOR) ? [scope] : Array.from(scope.querySelectorAll?.(ROOT_SELECTOR) || []);
    roots.forEach(hydrate);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
